import {
  countActiveAdvancedCriteria,
  isActiveAdvancedFilterValue,
  listAdvancedFilterActiveSummaryLines,
} from "./advancedFilter";
import { LIST_PAGE_SIZE_DEFAULT, normalizePageSize } from "./listPagination";
import {
  normalizeNavView,
  normalizeOpenClosedStatus,
  normalizeViewMode,
  setToStringArray,
  stringArrayToSet,
} from "./pageViewPreferenceState";

const TASK_VIEW_MODES = ["calendar", "table", "card"];

const TASK_PRIORITY_FILTER_LABELS = new Set(["Low", "Medium", "High", "Not set"]);

const TASK_NAV_VIEW_LABELS = {
  day: "Day",
  week: "Week",
  month: "Month",
  future: "Future",
};

function formatActiveSetFilterSummary(label, set) {
  if (!set || !(set instanceof Set) || set.size === 0) return "";
  const values = [...set].map((v) => String(v).trim()).filter(Boolean);
  if (values.length === 0) return "";
  return `${label}: ${values.join(", ")}`;
}

/** Drop inactive / legacy keys from saved task advanced filter criteria. */
export function sanitizeTaskAdvancedFilterCriteria(advancedFilter) {
  const raw = advancedFilter && typeof advancedFilter === "object" ? { ...advancedFilter } : {};
  delete raw.status;
  const out = {};
  for (const [key, value] of Object.entries(raw)) {
    if (isActiveAdvancedFilterValue(value)) out[key] = value;
  }
  return out;
}

function sanitizePriorityFilterArray(arr) {
  if (!Array.isArray(arr)) return [];
  return arr
    .map((x) => String(x).trim())
    .filter((x) => x && TASK_PRIORITY_FILTER_LABELS.has(x));
}

/** Flatten v2 { view, filters } saves; pass through legacy flat objects. */
function flattenTaskListPagePreferenceState(state) {
  if (!state || typeof state !== "object") return null;

  if (Number(state.version) >= 2 && state.view && typeof state.view === "object") {
    const view = state.view;
    const filters = state.filters && typeof state.filters === "object" ? state.filters : {};
    return {
      searchTerm: filters.searchTerm != null ? String(filters.searchTerm) : "",
      statusFilter: view.statusFilter,
      viewMode: view.viewMode,
      navView: view.navView,
      navDate: view.navDate != null ? String(view.navDate).trim() : "",
      advancedFilter: filters.advancedFilter && typeof filters.advancedFilter === "object"
        ? { ...filters.advancedFilter }
        : {},
      categoryFilter: Array.isArray(filters.categoryFilter) ? filters.categoryFilter : [],
      projectFilter: Array.isArray(filters.projectFilter) ? filters.projectFilter : [],
      priorityFilter: Array.isArray(filters.priorityFilter) ? filters.priorityFilter : [],
      scheduleStatusFilter: Array.isArray(filters.scheduleStatusFilter)
        ? filters.scheduleStatusFilter
        : [],
      vendorFilter: Array.isArray(filters.vendorFilter) ? filters.vendorFilter : [],
      scheduleDateFilter:
        filters.scheduleDateFilter != null ? String(filters.scheduleDateFilter).trim() : "",
      sortColumn: view.sortColumn != null ? String(view.sortColumn).trim() : null,
      sortDirection: view.sortDirection === "desc" ? "desc" : "asc",
      pageSize: view.pageSize,
    };
  }

  return state;
}

/**
 * Snapshot Task list toolbar filter/view state for persistence.
 * @param {object} source
 */
export function collectTaskListPagePreferenceState(source) {
  const {
    searchTerm,
    statusFilter,
    viewMode,
    navView,
    navDate,
    advancedFilter,
    categoryFilter,
    projectFilter,
    priorityFilter,
    scheduleStatusFilter,
    vendorFilter,
    scheduleDateFilter,
    sortColumn,
    sortDirection,
    pageSize,
  } = source || {};

  return {
    searchTerm: searchTerm != null ? String(searchTerm) : "",
    statusFilter: normalizeOpenClosedStatus(statusFilter) || "all",
    viewMode: normalizeViewMode(viewMode, TASK_VIEW_MODES) || "card",
    navView: normalizeNavView(navView) || "all",
    navDate:
      normalizeNavView(navView) === "all"
        ? ""
        : navDate != null
          ? String(navDate).trim()
          : "",
    advancedFilter: sanitizeTaskAdvancedFilterCriteria(advancedFilter),
    categoryFilter: setToStringArray(categoryFilter),
    projectFilter: setToStringArray(projectFilter),
    priorityFilter: sanitizePriorityFilterArray(setToStringArray(priorityFilter)),
    scheduleStatusFilter: setToStringArray(scheduleStatusFilter),
    vendorFilter: setToStringArray(vendorFilter),
    scheduleDateFilter: scheduleDateFilter != null ? String(scheduleDateFilter).trim() : "",
    sortColumn: sortColumn != null ? String(sortColumn).trim() : null,
    sortDirection: sortDirection === "desc" ? "desc" : "asc",
    pageSize: normalizePageSize(pageSize) ?? LIST_PAGE_SIZE_DEFAULT,
  };
}

/**
 * Apply saved Task list preference state via caller-provided setters.
 * @param {object} state
 * @param {object} handlers
 */
export function applyTaskListPagePreferenceState(state, handlers) {
  const flat = flattenTaskListPagePreferenceState(state);
  if (!flat || typeof flat !== "object" || !handlers) return;

  const {
    setSearchTerm,
    setStatusFilterSynced,
    setViewMode,
    setNavView,
    setNavDate,
    setAdvancedFilterApplied,
    setCategoryFilter,
    setProjectFilter,
    setPriorityFilter,
    setScheduleStatusFilter,
    setVendorFilter,
    setScheduleDateFilter,
    setSortColumn,
    setSortDirection,
    setPageSize,
  } = handlers;

  if (typeof setSearchTerm === "function") {
    setSearchTerm(flat.searchTerm != null ? String(flat.searchTerm) : "");
  }

  const status = normalizeOpenClosedStatus(flat.statusFilter);
  if (status && typeof setStatusFilterSynced === "function") {
    setStatusFilterSynced(status);
  }

  const mode = normalizeViewMode(flat.viewMode, TASK_VIEW_MODES);
  if (mode && typeof setViewMode === "function") {
    setViewMode(mode);
  }

  const navView = normalizeNavView(flat.navView);
  if (navView && typeof setNavView === "function") {
    setNavView(navView);
  }
  if (typeof setNavDate === "function") {
    const dateTrim = flat.navDate != null ? String(flat.navDate).trim() : "";
    if (navView === "all") {
      setNavDate("");
    } else if (dateTrim) {
      setNavDate(dateTrim);
    }
  }

  if (typeof setAdvancedFilterApplied === "function") {
    setAdvancedFilterApplied(sanitizeTaskAdvancedFilterCriteria(flat.advancedFilter));
  }

  if (typeof setCategoryFilter === "function") {
    setCategoryFilter(stringArrayToSet(flat.categoryFilter));
  }
  if (typeof setProjectFilter === "function") {
    setProjectFilter(stringArrayToSet(flat.projectFilter));
  }
  if (typeof setPriorityFilter === "function") {
    setPriorityFilter(stringArrayToSet(sanitizePriorityFilterArray(flat.priorityFilter)));
  }
  if (typeof setScheduleStatusFilter === "function") {
    setScheduleStatusFilter(stringArrayToSet(flat.scheduleStatusFilter));
  }
  if (typeof setVendorFilter === "function") {
    setVendorFilter(stringArrayToSet(flat.vendorFilter));
  }
  if (typeof setScheduleDateFilter === "function") {
    setScheduleDateFilter(
      flat.scheduleDateFilter != null ? String(flat.scheduleDateFilter).trim() : ""
    );
  }

  if (flat.sortColumn && typeof setSortColumn === "function") {
    setSortColumn(String(flat.sortColumn));
  }
  if (typeof setSortDirection === "function") {
    setSortDirection(flat.sortDirection === "desc" ? "desc" : "asc");
  }

  const size = normalizePageSize(flat.pageSize);
  if (size != null && typeof setPageSize === "function") {
    setPageSize(size);
  }
}

/** Default Task list filter state after clearing a saved default view. */
export function getDefaultTaskListSubFilterResetPatch() {
  return {
    searchTerm: "",
    advancedFilter: {},
    categoryFilter: new Set(),
    projectFilter: new Set(),
    priorityFilter: new Set(),
    scheduleStatusFilter: new Set(),
    vendorFilter: new Set(),
    scheduleDateFilter: "",
    statusFilter: "all",
  };
}

/**
 * Count non-default Task list filters for the toolbar badge (excludes search text).
 * @param {object} source
 */
export function countTaskListActiveFilters(source) {
  const {
    statusFilter,
    categoryFilter,
    projectFilter,
    priorityFilter,
    scheduleStatusFilter,
    vendorFilter,
    scheduleDateFilter,
    advancedFilter,
    navView,
  } = source || {};

  let count = 0;
  if (normalizeOpenClosedStatus(statusFilter) !== "all") count += 1;
  if (categoryFilter && categoryFilter.size > 0) count += 1;
  if (projectFilter && projectFilter.size > 0) count += 1;
  if (priorityFilter && priorityFilter.size > 0) count += 1;
  if (scheduleStatusFilter && scheduleStatusFilter.size > 0) count += 1;
  if (vendorFilter && vendorFilter.size > 0) count += 1;
  if (scheduleDateFilter != null && String(scheduleDateFilter).trim() !== "") count += 1;
  count += countActiveAdvancedCriteria(advancedFilter);
  if (normalizeNavView(navView) !== "all") count += 1;
  return count;
}

/**
 * One-line tooltip for active Task list filters (same scope as {@link countTaskListActiveFilters}).
 *
 * @param {object} source
 * @param {{ advancedFields?: Array<{ id: string, label?: string, type?: string, options?: Array<{ value: *, label?: string }> }> }} [options]
 * @returns {string}
 */
export function formatTaskListActiveFilterSummary(source, options = {}) {
  const {
    statusFilter,
    categoryFilter,
    projectFilter,
    priorityFilter,
    scheduleStatusFilter,
    vendorFilter,
    scheduleDateFilter,
    advancedFilter,
    navView,
  } = source || {};
  const advancedFields = Array.isArray(options.advancedFields) ? options.advancedFields : [];
  const parts = [];

  const status = normalizeOpenClosedStatus(statusFilter);
  if (status === "open" || status === "closed") {
    parts.push(`Status: ${status === "open" ? "Open" : "Closed"}`);
  }

  const categorySummary = formatActiveSetFilterSummary("Category", categoryFilter);
  if (categorySummary) parts.push(categorySummary);
  const projectSummary = formatActiveSetFilterSummary("Project", projectFilter);
  if (projectSummary) parts.push(projectSummary);
  const prioritySummary = formatActiveSetFilterSummary("Priority", priorityFilter);
  if (prioritySummary) parts.push(prioritySummary);
  const scheduleStatusSummary = formatActiveSetFilterSummary("Schedule status", scheduleStatusFilter);
  if (scheduleStatusSummary) parts.push(scheduleStatusSummary);
  const vendorSummary = formatActiveSetFilterSummary("Vendor", vendorFilter);
  if (vendorSummary) parts.push(vendorSummary);

  const scheduleDate =
    scheduleDateFilter != null && String(scheduleDateFilter).trim() !== ""
      ? String(scheduleDateFilter).trim()
      : "";
  if (scheduleDate) parts.push(`Schedule date: ${scheduleDate}`);

  parts.push(...listAdvancedFilterActiveSummaryLines(advancedFilter, advancedFields));

  const nav = normalizeNavView(navView);
  if (nav && nav !== "all") {
    parts.push(`Time range: ${TASK_NAV_VIEW_LABELS[nav] || nav}`);
  }

  return parts.join(" AND ");
}
